"use client";

import clsx from "clsx";
import { useRef, useState, useEffect } from "react";
import BlurredDecorations from "@/components/blurred-decorations/blurred-decorations";
import RecordsGalleryGroup from "@/components/records/records-gallery/records-gallery-group";
import RecordsGalleryGroupMobile from "@/components/records/records-gallery/records-gallery-group-mobile";
import RecordsGalleryBackground from "@/components/records/records-gallery-background/records-gallery-background";
import RecordsGalleryHero from "@/components/records/records-gallery-hero/records-gallery-hero";
import Section from "@/components/section";
import { CATEGORY_SLUGS } from "@/constants/curated-story-categories";
import { ASSET_PREFIX } from "@/constants/routes"
import { useApp } from "@/context/AppContext";
import RecordsStickyNav from "../records-sticky-nav/records-sticky-nav";

const BATCH_SIZE = 12;

const images = [
  {
    id: CATEGORY_SLUGS.CULTURE_AND_HERITAGE,
    url: {
      desktop: `${ASSET_PREFIX}/img/categories-bg/desktop/culture-and-heritage.jpg`,
      mobile: `${ASSET_PREFIX}/img/categories-bg/mobile/culture-and-heritage.jpg`,
    },
  },
  {
    id: CATEGORY_SLUGS.LABOR_AND_LIVELIHOOD,
    url: {
      desktop: `${ASSET_PREFIX}/img/categories-bg/desktop/labor-and-livelihood.jpg`,
      mobile: `${ASSET_PREFIX}/img/categories-bg/mobile/labor-and-livelihood.jpg`,
    },
  },
  {
    id: CATEGORY_SLUGS.ICONS_AND_INFLUENCES,
    url: {
      desktop: `${ASSET_PREFIX}/img/categories-bg/desktop/icons-and-influences.jpg`,
      mobile: `${ASSET_PREFIX}/img/categories-bg/mobile/icons-and-influences.jpg`,
    },
  },
  {
    id: CATEGORY_SLUGS.SERVICE_AND_SACRIFICE,
    url: {
      desktop: `${ASSET_PREFIX}/img/categories-bg/desktop/service.jpg`,
      mobile: `${ASSET_PREFIX}/img/categories-bg/mobile/service.jpg`,
    },
  },
  {
    id: CATEGORY_SLUGS.INNOVATORS_AND_VISIONARIES,
    url: {
      desktop: `${ASSET_PREFIX}/img/categories-bg/desktop/visionaries.jpg`,
      mobile: `${ASSET_PREFIX}/img/categories-bg/mobile/visionaries.jpg`,
    },
  },
];

export default function RecordsGallery({ hasStickyNav = false, isDummy = false, recordCategoryType, isTopSection = false, hasColParallax = true, slug, blurredDecorationCount = 4, blurredDecorationStartIndex = 0, offsetDir = 1, cards, theme = "main-light", headingLevel = "h1", subheadingLevel = "h2", title, label, intro, introClassName }) {
  const image = images.find((img) => img.id === slug);
  const columnsRef = useRef(null);
  const { isMobile, currentTransitionName, getSavedScrollPosition } = useApp();
  const [pct] = useState(0);
  const hasRestoredScroll = useRef(false);

  // Restore scroll position when returning from story overlay
  useEffect(() => {
    if (currentTransitionName === 'overlay-out' && !hasRestoredScroll.current) {
      const savedPosition = getSavedScrollPosition();
      if (savedPosition !== null) {
        hasRestoredScroll.current = true;
        window.scrollTo(0, savedPosition);
      }
    }
    // Reset flag when transition changes away from overlay-out
    if (currentTransitionName !== 'overlay-out') {
      hasRestoredScroll.current = false;
    }
  }, [currentTransitionName, getSavedScrollPosition]);


  const content = (
    <div className="flex flex-col items-center mx-auto xl:pt-9 max-md:w-full max-md:px-4 pb-[2.125rem] md:pb-[4.125rem] md:pb-[6.625rem]">
      {image && <RecordsGalleryBackground image={image} />}
      <RecordsGalleryHero
        title={title}
        label={label}
        intro={intro}
        introClassName={introClassName}
        headingLevel={headingLevel}
        subheadingLevel={subheadingLevel}
      />
      <div className="max-md:flex max-md:flex-col gap-y-20 lg:gap-y-14 xl:gap-y-[6.625rem] md:grid-3 relative" ref={columnsRef}>
        <BlurredDecorations count={blurredDecorationCount} isFullPage={!image} categoryId={slug} startIndex={blurredDecorationStartIndex} />
        {!isMobile && <RecordsGalleryGroup
          recordCategoryType={recordCategoryType}
          pct={pct}
          batchSize={BATCH_SIZE}
          cards={cards}
          offsetDir={offsetDir}
        />}

        {isMobile && <RecordsGalleryGroupMobile
          recordCategoryType={recordCategoryType}
          pct={pct}
          batchSize={BATCH_SIZE}
          cards={cards}
          offsetDir={offsetDir}
        />}
        {/* {batches.map((batch, groupIndex) => (
          ))} */}
      </div>

    </div>
  );
  
  return (
    <Section theme={theme} className={clsx("pt-header overflow-x-clip", !isDummy && 'pb-(--space-10)')} isTopSection={isTopSection}>
      {isDummy ? (
        <div className="max-height-dummy-section">
          {content}
        </div>
      ) : (
        content
      )}
      {hasStickyNav && <RecordsStickyNav isSticky={true} />}
    </Section>
  );
}
